import { useApiTestingLanguage } from './useApiTestingLanguage';
import React from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { CheckCircle, Folder } from 'lucide-react';

export const RequestTabContent = ({ testForm, setTestForm, environments, projects }) => {
  const { t } = useApiTestingLanguage();
  return (
  <>
    <div className="grid grid-cols-3 gap-4">
      <div className="col-span-2">
        <label className="text-sm font-medium text-gray-700 block mb-2">
          {t("Test Name")} <span className="text-red-500">*</span>
        </label>
        <Input
          placeholder={t("User API Test")}
          value={testForm.name}
          onChange={(e) => setTestForm({...testForm, name: e.target.value})}
          data-testid="input-test-name"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-2">
          {t("Method")} <span className="text-red-500">*</span>
        </label>
        <select
          value={testForm.method}
          onChange={(e) => setTestForm({...testForm, method: e.target.value})}
          className="w-full p-2 border border-gray-300 rounded-md h-10"
          data-testid="select-method"
        >
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="DELETE">DELETE</option>
          <option value="PATCH">PATCH</option>
        </select>
      </div>
    </div>

    <div>
      <label className="text-sm font-medium text-gray-700 block mb-2">
        URL <span className="text-red-500">*</span>
      </label>
      <Input
        placeholder="https://api.example.com/users"
        value={testForm.url}
        onChange={(e) => setTestForm({...testForm, url: e.target.value})}
        className="font-mono text-sm"
        data-testid="input-url"
      />
      <p className="text-xs text-gray-500 mt-1">{t("Use")} {'{{variable}}'} {t("syntax for environment variables")}</p>
    </div>

    <div>
      <label className="text-sm font-medium text-gray-700 block mb-2">
        {t("Description")}
      </label>
      <Textarea
        placeholder={t("Describe what this test validates...")}
        value={testForm.description}
        onChange={(e) => setTestForm({...testForm, description: e.target.value})}
        rows={3}
        data-testid="input-description"
      />
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-2">
          {t("Environment")}
        </label>
        <select
          value={testForm.environment_id}
          onChange={(e) => setTestForm({...testForm, environment_id: e.target.value})}
          className="w-full p-2 border border-gray-300 rounded-md"
          data-testid="select-environment"
        >
          <option value="">{t("None")}</option>
          {environments.map(env => (
            <option key={env.id} value={env.id}>{env.name}</option>
          ))}
        </select>
      </div>

      {projects.length > 0 && (
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-2 flex items-center">
            <Folder className="h-4 w-4 mr-2 text-purple-600" />
            {t("Project")}
          </label>
          <select
            value={testForm.project_id || ''}
            onChange={(e) => setTestForm({...testForm, project_id: e.target.value || null})}
            className="w-full p-2 border border-gray-300 rounded-md"
            data-testid="select-project"
          >
            <option value="">{t("No Project (Uncategorized)")}</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.icon} {project.name}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  </>
);
};

/**
 * Auth Tab - Credential selection
 */
export const AuthTabContent = ({ testForm, setTestForm, credentials }) => {
  const { t } = useApiTestingLanguage();
  return (
  <div className="bg-gray-50 rounded-lg p-6">
    <h3 className="text-sm font-medium text-gray-700 mb-4">{t("Authentication")}</h3>
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-gray-700 block mb-2">
          {t("Select Credential")}
        </label>
        <select
          value={testForm.credential_id}
          onChange={(e) => {
            const selectedCredId = e.target.value;
            const selectedCred = credentials.find(c => c.id === selectedCredId);
            setTestForm({
              ...testForm,
              credential_id: selectedCredId,
              auth_type: selectedCred ? selectedCred.type : ''
            });
          }}
          className="w-full p-2 border border-gray-300 rounded-md"
          data-testid="select-credential"
        >
          <option value="">{t("No Authentication")}</option>
          {credentials.map(cred => (
            <option key={cred.id} value={cred.id}>{cred.name} ({cred.type})</option>
          ))}
        </select>
      </div>

      {testForm.credential_id && (
        <div className="bg-white rounded border p-4">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-gray-700">
              {t("Using credential:")} <strong>{credentials.find(c => c.id === testForm.credential_id)?.name}</strong>
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {t("Type:")} {credentials.find(c => c.id === testForm.credential_id)?.type}
          </p>
        </div>
      )}

      {!testForm.credential_id && (
        <p className="text-sm text-gray-500">
          {t("No authentication configured. Create credentials in the Credentials tab if needed.")}
        </p>
      )}
    </div>
  </div>
);
};

/**
 * Headers Tab - Request headers with autocomplete and quick-add
 */
